import { createContext, useContext, useEffect } from 'react';

// WHO CAN OPEN THE PALETTE (REQ-042 AC1). The shell owns the open/closed state and puts this here, so the
// two affordances that reach it — the top bar's button and the More sheet's row below 768 px — are two
// triggers for ONE surface rather than two palettes that could disagree.

export interface SearchController {
  open: boolean;
  openSearch: () => void;
  closeSearch: () => void;
}

export const SearchContext = createContext<SearchController | null>(null);

/** Inside the shell this is always there; outside it there is no palette to open. */
export function useSearchController(): SearchController | null {
  return useContext(SearchContext);
}

/** An editable target swallows `/` — a slash typed into the list's own search field is a slash. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * REQ-042 AC1's two shortcuts, on one document listener:
 *
 *   · **Ctrl/Cmd + K** always opens it, wherever the focus is — it is the shortcut everyone tries first, and
 *     a browser that would otherwise focus its own address bar is told not to.
 *   · **`/`** opens it only when the focus is NOT in something you type into, because inside the list's
 *     search field, a budget input or a text area a slash is a character.
 *
 * Nothing is captured while the palette is already open: the palette's own input takes the keys from there.
 */
export function useSearchShortcuts(enabled: boolean, onOpen: () => void): void {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const meta = event.metaKey || event.ctrlKey;
      if (meta && !event.altKey && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        onOpen();
        return;
      }
      if (event.key === '/' && !meta && !event.altKey && !isEditableTarget(event.target)) {
        event.preventDefault();
        onOpen();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, onOpen]);
}

/** `⌘ K` on a Mac, `Ctrl K` everywhere else — the hint has to name the key the reader actually has. */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform || '';
  return /Mac|iPhone|iPad|iPod/i.test(platform) || /Mac OS X/i.test(navigator.userAgent || '');
}
